import { useEffect, useState, type ReactNode } from 'react';
import { create } from 'zustand';
import { Link } from 'wouter';
import type { NavCtx, NavItem } from './navConfig';

const OPEN_KEY = 'ed.nav.open';

function readOpen(): string[] {
  try {
    const raw = localStorage.getItem(OPEN_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

/**
 * Which rail sections the member has opened, persisted across visits. Read
 * synchronously at store creation so the rail paints in its final state on
 * first render (no open/close flash, no layout shift).
 */
export const useNavOpen = create<{ open: string[]; toggle: (id: string) => void }>((set, get) => ({
  open: typeof window === 'undefined' ? [] : readOpen(),
  toggle: (id) => {
    const cur = get().open;
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    try {
      localStorage.setItem(OPEN_KEY, JSON.stringify(next));
    } catch {
      /* private mode: keep in memory only */
    }
    set({ open: next });
  },
}));

/**
 * A collapsible rail section: a real <button> header (name, live count,
 * chevron) over an animated body. The section holding the current route is
 * opened automatically; the member can still close it, and it reopens the next
 * time they navigate into it.
 */
export function NavSection({
  id,
  label,
  hint,
  count,
  containsActive,
  children,
}: {
  id: string;
  label: string;
  hint: string;
  count: number;
  containsActive: boolean;
  children: ReactNode;
}) {
  const open = useNavOpen((s) => s.open.includes(id));
  const toggle = useNavOpen((s) => s.toggle);
  // Lets the member close the section they're currently in. Cleared whenever
  // they navigate into it afresh (containsActive flips false → true).
  const [closedHere, setClosedHere] = useState(false);
  useEffect(() => {
    if (!containsActive) setClosedHere(false);
  }, [containsActive]);

  const isOpen = containsActive ? !closedHere : open;
  const onClick = () => {
    if (containsActive) setClosedHere((v) => !v);
    else toggle(id);
  };

  return (
    <div className={`nsec${isOpen ? ' is-open' : ''}${containsActive ? ' is-here' : ''}`} data-nsec={id}>
      <button
        type="button"
        className="nsec__hd"
        aria-expanded={isOpen}
        aria-controls={`nsec-${id}`}
        title={hint}
        onClick={onClick}
      >
        <span className="nsec__label">{label}</span>
        <span className="nsec__ct" aria-label={`${count} features`}>
          {count}
        </span>
        <span className="nsec__ch" aria-hidden="true" />
      </button>
      {/* `inert` keeps collapsed links out of the tab order. */}
      <div className="nsec__body" id={`nsec-${id}`} inert={!isOpen}>
        <div>{children}</div>
      </div>
    </div>
  );
}

/** One nav row, shared by the rail's start group and every section. */
export function NavRow({ item, ctx, onNavigate }: { item: NavItem; ctx: NavCtx; onNavigate?: () => void }) {
  const active = item.isActive(ctx);
  return (
    <Link
      href={item.href}
      title={item.title}
      className={`sidebar__item${active ? ' is-active' : ''}${item.soon ? ' is-soon' : ''}`}
      aria-current={active ? 'page' : undefined}
      data-guide={item.key}
      onClick={onNavigate}
    >
      <span className="sidebar__item-num" aria-hidden="true">
        {item.glyph}
      </span>
      <span className="sidebar__item-label">{item.label}</span>
      {item.soon && <span className="sidebar__soon">Soon</span>}
    </Link>
  );
}
